import Service, { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';
import { restartableTask } from 'ember-concurrency';
import BufferIconCell from 'volta/components/cell/buffer-icon-cell';
import CodeDescCell from 'volta/components/cell/code-desc-cell';
import LabelCell from 'volta/components/cell/label-cell';
import PlanningPriorityCell from 'volta/components/cell/planning-priority-cell';
import StatusCell from 'volta/components/cell/status-cell';
import AlertOptionComponent from 'volta/components/filters/alert-option';
import BooleanFilter from 'volta/components/filters/boolean-filter';
import BufferZoneOptionComponent from 'volta/components/filters/buffer-zone-option';
import DateFilter from 'volta/components/filters/date-filter';
import InputFilter from 'volta/components/filters/input-filter';
import PriorityFilter from 'volta/components/filters/priority-filter';
import SelectFilter from 'volta/components/filters/select-filter';
import SkusPartTypeFilter from 'volta/components/filters/skus-part-type-filter';
import SkusSupplierFilter from 'volta/components/filters/skus-supplier-filter';
import SkusWarehouseFilter from 'volta/components/filters/skus-warehouse-filter';
import SkusWorkshopFilter from 'volta/components/filters/skus-workshop-filter';
import StatusFilter from 'volta/components/filters/status-filter';
import TokenFilter from 'volta/components/filters/token-filter';
import POWorkbenchCvArgsEditor from 'volta/components/po-workbench/collection-view-args-editor';
import POUserFilter from 'volta/components/po-workbench/filters/user-filter';
import {
  CollectionViewEvents,
  CollectionViewResourceName,
  columnsToProps,
  columnsToSortOptions
} from 'volta/models/collection-view';
import { AcquisitionCodesOptions } from 'volta/models/constants/acquisition-codes';
import { BufferZonesOptions } from 'volta/models/constants/buffer-zones';
import { LeadTimeCategoriesOptions } from 'volta/models/constants/lead-time-categories';
import { PlanningMethodologiesOptions } from 'volta/models/constants/planning-methodologies';
import { ReorderStrategiesOptions } from 'volta/models/constants/reorder-strategies';
import { VarCategoriesOptions } from 'volta/models/constants/var-categories';
import { buildCustomFieldColumn } from 'volta/models/custom-field-definition';
import {
  POValidationStatusesArray,
  SchedulingAlertOptions,
  POStatusOptions,
  SupplierPortalStatuses,
  SyncAlertOptions,
  VisibleStatusOptions,
  SyncStatusOptions
} from 'volta/models/planning-order';
import { POGroupResourceName } from 'volta/models/po-group';
import { normalize } from 'volta/utils/api/serialize-and-push';
import { indexByFn } from 'volta/utils/array-utils';
import { formatLocalDate } from 'volta/utils/date-utils';
import {
  beforeQueryWorkshop,
  collectionFiltersFromQueryParams,
  serializeArrayValues,
  translateFilterOptions
} from 'volta/utils/filters-utils';

import type Owner from '@ember/owner';
import type RouterService from '@ember/routing/router-service';
import type { IntlService } from 'ember-intl';
import type { IBvTableColumn } from 'volta/components/bv-table';
import type CollectionView from 'volta/models/collection-view';
import type PlanningOrder from 'volta/models/planning-order';
import type {
  ICollectionDefinitionBuilder,
  ICollectionSortOption,
  ICollectionViewDefinition,
  ICreateCollectionViewCmd
} from 'volta/models/types/collection-view';
import type CustomFieldDefinitionsService from 'volta/services/custom-field-definitions-service';
import type LabelService from 'volta/services/label-service';
import type SessionUserService from 'volta/services/session-user';
import type StoreService from 'volta/services/store';
import type { IJsonApiQuery, SingleResourceDoc } from 'volta/utils/api/jsonapi-types';
import type {
  ICollectionFilter,
  IPOWorkbenchCollectionArgs
} from '../models/types/collection-view.d';
import type EventStreamService from './event-stream';
import { TimeBufferAlertOptions } from 'volta/models/constants/time-buffer-alerts';
import type AppSettings from './app-settings';

export const DefaultSettings: Required<IPOWorkbenchCollectionArgs> = {
  showCapacity: false,
  showCapacityChart: true,
  codeDescMode: ['desc'],
  showSkuDesc: true,
  dateField: 'dueDate'
};

export const DEFAULT_STATUSES = [
  POStatusOptions.LAUNCHED.value,
  POStatusOptions.COMMITTED.value,
  POStatusOptions.SENT.value,
  POStatusOptions.DRAFT.value
];

export const DEFAULT_STATUSES_SUPPLIER = [
  POStatusOptions.LAUNCHED.value,
  POStatusOptions.COMMITTED.value,
  POStatusOptions.SENT.value
];

export default class POGroupsCollectionDefinitionBuilder
  extends Service
  implements ICollectionDefinitionBuilder
{
  @service store!: StoreService;
  @service customFieldDefinitionsService!: CustomFieldDefinitionsService;
  @service intl!: IntlService;
  @service labelService!: LabelService;
  @service sessionUser!: SessionUserService;
  @service router!: RouterService;
  @service eventStream!: EventStreamService;
  @service appSettings!: AppSettings;

  @tracked myCollectionViews?: CollectionView[];

  get isSup() {
    return this.sessionUser.isSupplierUser;
  }

  @cached
  get definitionsAndColumns() {
    return {
      definitions: [
        ...this.customFieldDefinitionsService.poDefinitions,
        ...this.customFieldDefinitionsService.skusDefinitions
      ],
      columns: this.buildColumns()
    };
  }

  constructor(owner: Owner) {
    super(owner);
    this.eventStream.addHandler(
      CollectionViewResourceName.plural,
      CollectionViewEvents.CollectionViewCreated,
      this._handleCollectionViewCreated,
      this
    );
    this.eventStream.addHandler(
      CollectionViewResourceName.plural,
      CollectionViewEvents.CollectionViewUpdated,
      this._handleCollectionViewUpdated,
      this
    );
    this.eventStream.addHandler(
      CollectionViewResourceName.plural,
      CollectionViewEvents.CollectionViewDeleted,
      this._handleCollectionViewDeleted,
      this
    );
    this.customFieldDefinitionsService.fetchPODefinitions();
  }

  buildColumns() {
    const { intl, customFieldDefinitionsService, isSup, router } = this;
    const cols: IBvTableColumn[] = [];

    const forAll = (col: IBvTableColumn) => {
      cols.push(col);
    };

    const notForSupplier = (col: IBvTableColumn) => {
      if (!isSup) {
        cols.push(col);
      }
    };

    forAll({
      columnName: intl.t('priority'),
      sortKey: 'priority',
      valuePath: 'priority',
      width: 50,
      isReorderable: true,
      isResizable: false
    });

    forAll({
      columnName: intl.t('partCode'),
      sortKey: 'part',
      valuePath: 'part',
      width: 250,
      minWidth: 100,
      cellComponent: CodeDescCell,
      color: 'blue',
      isGroupable: !isSup,
      format: (model: PlanningOrder) => {
        return {
          code: model.skuInfo.part.code,
          description: isSup ? model.skuInfo.supplierSkuDescription : model.skuInfo.part.description
        };
      }
    });

    forAll({
      columnName: intl.t('part'),
      sortKey: 'skuDescription',
      valuePath: 'sku',
      width: 250,
      minWidth: 100,
      cellComponent: CodeDescCell,
      isFixed: 'left',
      color: 'blue',
      isGroupable: false,
      format: (model: PlanningOrder) => {
        return {
          link: 'protected.skus.sku',
          linkParam: model.id,
          code: model.skuInfo.part.code,
          description: isSup ? model.skuInfo.supplierSkuDescription : model.skuInfo.part.description
        };
      }
    });

    notForSupplier({
      columnName: intl.t('routeOperation'),
      valuePath: 'operationName',
      sortKey: 'operationName',
      width: 80,
      isReorderable: true,
      isResizable: true,
      isGroupable: !isSup
    });

    notForSupplier({
      columnName: intl.t('operationStarted'),
      valuePath: 'operationStarted',
      sortKey: 'operationStarted',
      width: 90,
      isReorderable: true,
      isResizable: true,
      isGroupable: false,
      format: (po: PlanningOrder) => {
        return intl.t(po.operationStarted ? 'yes' : 'no');
      }
    });

    notForSupplier({
      columnName: intl.t('warehouse'),
      sortKey: 'warehouseCode',
      valuePath: 'warehouse',
      width: 100,
      minWidth: 50,
      isReorderable: true,
      isResizable: true,
      isGroupable: !isSup,
      color: 'green',
      cellComponent: LabelCell,
      format: ({ skuInfo }: PlanningOrder) => {
        return {
          label: skuInfo.warehouse.code,
          color: 'green',
          iconRight: 'link-outline',
          tooltipText: skuInfo.warehouse.description,
          onClick: isSup
            ? undefined
            : () => router.transitionTo('protected.warehouse', skuInfo.warehouse.warehouseId)
        };
      }
    });

    notForSupplier({
      columnName: intl.t('partType'),
      sortKey: 'partType',
      valuePath: 'partType',
      width: 100,
      minWidth: 50,
      isReorderable: true,
      isResizable: true,
      isGroupable: !isSup,
      color: 'indigo',
      cellComponent: LabelCell,
      format: (order: PlanningOrder) => {
        return {
          color: 'indigo',
          label: order.skuInfo.partType?.code,
          tooltipText: order.skuInfo.partType?.description
        };
      }
    });

    forAll({
      columnName: intl.t('status'),
      sortKey: 'status',
      valuePath: 'status',
      minWidth: 60,
      isGroupable: true,
      isStatic: true,
      cellComponent: StatusCell,
      format: (po: PlanningOrder) => {
        return po.statusOption;
      }
    });

    notForSupplier({
      columnName: intl.t('poWorkbench.alerts.scheduling.title'),
      sortKey: 'schedulingAlert',
      valuePath: 'schedulingAlert',
      minWidth: 80,
      cellComponent: StatusCell,
      format: (po: PlanningOrder) => {
        return po.schedulingAlertOption;
      }
    });

    notForSupplier({
      columnName: intl.t('orderSyncAlert'),
      sortKey: 'syncAlert',
      valuePath: 'syncAlert',
      minWidth: 80,
      cellComponent: StatusCell,
      format: (po: PlanningOrder) => {
        return po.syncAlertOption;
      }
    });

    notForSupplier({
      columnName: intl.t('timeBufferAlert'),
      sortKey: 'timeBufferAlert',
      valuePath: 'timeBufferAlert',
      isGroupable: true,
      minWidth: 80,
      cellComponent: StatusCell,
      format: (po: PlanningOrder) => {
        return po.timeBufferAlertOption;
      }
    });

    notForSupplier({
      columnName: intl.t('supplier'),
      sortKey: 'supplierCode',
      valuePath: 'supplier',
      width: 100,
      minWidth: 50,
      isReorderable: true,
      isResizable: true,
      isGroupable: !isSup,
      color: 'primary',
      cellComponent: LabelCell,
      format: (po: PlanningOrder) => {
        return {
          label: po.supplierInfo?.code,
          color: 'primary',
          iconRight: 'link-outline',
          tooltipText: po.supplierInfo?.description,
          onClick: isSup
            ? undefined
            : () =>
                po.supplierInfo &&
                router.transitionTo('protected.supplier', po.supplierInfo.supplierId)
        };
      }
    });

    notForSupplier({
      columnName: intl.t('workshop'),
      sortKey: 'workshopCode',
      valuePath: 'workshop',
      width: 100,
      minWidth: 50,
      isReorderable: true,
      isResizable: true,
      isGroupable: !isSup,
      color: 'orange',
      cellComponent: LabelCell,
      format: (po: PlanningOrder) => {
        return {
          label: po.workshopInfo?.code,
          color: 'orange',
          iconRight: 'link-outline',
          tooltipText: po.workshopInfo?.description,
          onClick: isSup
            ? undefined
            : () =>
                po.workshopInfo &&
                router.transitionTo('protected.workshop', po.workshopInfo.workshopId)
        };
      }
    });

    notForSupplier({
      columnName: intl.t('deliveryWorkshop'),
      sortKey: 'deliveryWorkshopCode',
      valuePath: 'deliveryWorkshop',
      width: 100,
      minWidth: 50,
      isReorderable: true,
      isResizable: true,
      isGroupable: !isSup,
      color: 'purple',
      cellComponent: LabelCell,
      format: (po: PlanningOrder) => {
        return {
          label: po.deliveryWorkshopInfo?.code,
          color: 'purple',
          iconRight: 'link-outline',
          tooltipText: po.deliveryWorkshopInfo?.description,
          onClick: isSup
            ? undefined
            : () =>
                po.deliveryWorkshopInfo &&
                router.transitionTo('protected.workshop', po.deliveryWorkshopInfo.workshopId)
        };
      }
    });
    notForSupplier({
      columnName: intl.t('planningMethodology'),
      valuePath: 'methodology',
      sortKey: 'methodology',
      width: 60,
      cellComponent: BufferIconCell,
      isReorderable: true,
      isResizable: true,
      isGroupable: !isSup,
      isStatic: true,
      format: (po: PlanningOrder) => {
        return {
          methodology: po.skuInfo.methodology,
          reorderStrategy: po.skuInfo.reorderStrategy
        };
      }
    });

    notForSupplier({
      columnName: intl.t('acqCodeShort'),
      valuePath: 'acqCode',
      sortKey: 'acqCode',
      width: 80,
      isReorderable: true,
      isResizable: true,
      isGroupable: !isSup,
      cellComponent: LabelCell,
      format: (po: PlanningOrder) => {
        return {
          label: intl.t(`acqCodes.${po.acqCode ?? 'UNKNOWN'}`)
        };
      }
    });

    forAll({
      columnName: intl.t('reorderQty'),
      sortKey: 'reorderQty',
      valuePath: 'reorderQty',
      width: 75,
      textAlign: 'right',
      isReorderable: true,
      isResizable: true,
      isStatic: true,
      classNames: 'u-text-numeric',
      format: (po: PlanningOrder) => {
        return intl.formatNumber(po.reorderQty);
      }
    });

    forAll({
      columnName: intl.t('unitPrice'),
      sortKey: 'unitPrice',
      valuePath: 'unitPrice',
      width: 75,
      textAlign: 'right',
      isReorderable: true,
      isResizable: true,
      classNames: 'u-text-mono',
      format: (po: PlanningOrder) => {
        return po.formattedUnitPrice;
      }
    });

    forAll({
      columnName: intl.t('totalPrice'),
      sortKey: 'totalPrice',
      valuePath: 'totalPrice',
      width: 75,
      textAlign: 'right',
      isReorderable: true,
      isResizable: true,
      classNames: 'u-text-mono',
      format: (po: PlanningOrder) => {
        return po.formattedTotalPrice;
      }
    });

    forAll({
      columnName: intl.t('dueDate'),
      sortKey: 'dueDate',
      valuePath: 'dueDate',
      width: 75,
      isReorderable: true,
      isResizable: true,
      isStatic: true,
      format: (po: PlanningOrder) => {
        return formatLocalDate(po.dueDate);
      }
    });
    forAll({
      columnName: intl.t('deliveryDate'),
      sortKey: 'deliveryDate',
      valuePath: 'deliveryDate',
      width: 75,
      isReorderable: true,
      isResizable: true,
      isStatic: true,
      format: (po: PlanningOrder) => {
        return formatLocalDate(po.deliveryDate);
      }
    });
    forAll({
      columnName: intl.t('launchDate'),
      sortKey: 'launchDate',
      valuePath: 'launchDate',
      width: 75,
      isReorderable: true,
      isResizable: true,
      isStatic: true,
      isGroupable: false,
      format: (po: PlanningOrder) => {
        return formatLocalDate(po.launchDate);
      }
    });
    forAll({
      columnName: intl.t('reorderDate'),
      sortKey: 'reorderDate',
      valuePath: 'reorderDate',
      width: 75,
      isReorderable: true,
      isResizable: true,
      isStatic: true,
      format: (po: PlanningOrder) => {
        return formatLocalDate(po.reorderDate);
      }
    });
    forAll({
      columnName: intl.t('createdBy'),
      sortKey: 'createdBy',
      valuePath: 'createdBy',
      width: 40,
      isReorderable: true,
      isResizable: true,
      isStatic: true,
      format: (po: PlanningOrder) => {
        return po.createdBy;
      }
    });
    forAll({
      columnName: intl.t('orderNum'),
      sortKey: 'orderNum',
      valuePath: 'orderNum',
      width: 40,
      isReorderable: true,
      isResizable: true,
      isGroupable: true,
      color: 'yellow-6',
      format: (po: PlanningOrder) => {
        return po.orderNum;
      }
    });
    forAll({
      columnName: intl.t('orderLine'),
      sortKey: 'orderLine',
      valuePath: 'orderLine',
      width: 40,
      isReorderable: true,
      isResizable: true,
      isGroupable: true,
      color: 'yellow-6',
      format: (po: PlanningOrder) => {
        return po.orderLine ?? po.voltaOrderLine?.toString();
      }
    });
    forAll({
      columnName: intl.t('externalOrderNum'),
      sortKey: 'externalOrderNum',
      valuePath: 'externalOrderNum',
      width: 40,
      isReorderable: true,
      isResizable: true,
      isGroupable: false,
      color: 'yellow-8',
      format: (po: PlanningOrder) => {
        return po.externalOrderNum;
      }
    });
    notForSupplier({
      columnName: intl.t('planningBuffer'),
      sortKey: 'planningBuffer',
      valuePath: 'planningBuffer',
      width: 120,
      isReorderable: true,
      isResizable: true,
      isGroupable: false,
      cellComponent: PlanningPriorityCell,
      format: (model: PlanningOrder) => {
        return {
          priorityName: model.priorityName,
          bufferPenetration: model.planningInfo.bufferPenetration,
          route: 'protected.po-workbench.sku',
          routeModel: model.skuId
        };
      }
    });
    forAll({
      columnName: intl.t('executionBuffer'),
      sortKey: 'executionBuffer',
      valuePath: 'executionBuffer',
      width: 120,
      isReorderable: true,
      isResizable: true,
      isGroupable: false,
      cellComponent: PlanningPriorityCell,
      format: (model: PlanningOrder) => {
        return {
          priorityName: model.executionPriorityName,
          bufferPenetration: model.executionInfo.oha,
          route: 'protected.po-workbench.sku',
          routeModel: model.skuId
        };
      }
    });

    notForSupplier({
      columnName: intl.t('abcClass'),
      valuePath: 'abcClass',
      sortKey: 'abcClass',
      width: 80,
      isReorderable: true,
      isResizable: true,
      isGroupable: !isSup
    });

    notForSupplier({
      columnName: intl.t('xyzClass'),
      valuePath: 'xyzClass',
      sortKey: 'xyzClass',
      width: 80,
      isReorderable: true,
      isResizable: true,
      isGroupable: !isSup
    });

    notForSupplier({
      columnName: intl.t('leadTimeCategory'),
      valuePath: 'leadTimeCategory',
      sortKey: 'leadTimeCategory',
      width: 80,
      isReorderable: true,
      isResizable: true,
      isGroupable: !isSup
    });

    notForSupplier({
      columnName: intl.t('varCategory'),
      valuePath: 'varCategory',
      sortKey: 'varCategory',
      width: 80,
      isReorderable: true,
      isResizable: true,
      isGroupable: !isSup
    });

    notForSupplier({
      columnName: intl.t('syncStatus'),
      valuePath: 'syncStatus',
      sortKey: 'syncStatus',
      width: 80,
      isReorderable: true,
      isResizable: true,
      isGroupable: false
    });

    const customFieldProps: (IBvTableColumn | undefined)[] = (
      customFieldDefinitionsService.poCustomProperties() ?? []
    ).map((def) =>
      buildCustomFieldColumn(def, (model: PlanningOrder) => {
        const dataType = def.dataType ? def.dataType.toLowerCase() : undefined;
        const { customFields } = model;

        const value = customFields ? customFields[def.property] : undefined;
        return {
          value,
          dataType
        };
      })
    );

    const skuCustomFieldProps: (IBvTableColumn | undefined)[] = (
      customFieldDefinitionsService.skusCustomProperties('sku') ?? []
    ).map((def) =>
      buildCustomFieldColumn(def, (model: PlanningOrder) => {
        const dataType = def.dataType ? def.dataType.toLowerCase() : undefined;
        const {
          skuInfo: { customFields }
        } = model;

        const value = customFields ? customFields[def.property] : undefined;
        return {
          value,
          dataType
        };
      })
    );

    return [
      ...cols,
      ...(customFieldProps.filter(Boolean) as IBvTableColumn[]),
      ...(isSup ? [] : (skuCustomFieldProps.filter(Boolean) as IBvTableColumn[]))
    ];
  }

  buildFilters(queryParamsValues: TQueryParams) {
    const { store, customFieldDefinitionsService, isSup, sessionUser, intl } = this;
    const { filters } = queryParamsValues;
    const cvFilters = indexByFn(
      collectionFiltersFromQueryParams(filters as string),
      (f) => f.property
    );
    const warehouseIds = cvFilters['warehouse']?.values;
    const createdBy = cvFilters['createdBy']?.values
      ? cvFilters['createdBy'].values.join(',')
      : undefined;
    const normalDefs: TFilterDefinition[] = [];

    const notForSupplier = (filter: TFilterDefinition) => {
      if (!isSup) {
        normalDefs.push(filter);
      }
    };

    normalDefs.push(
      {
        queryParam: 'status',
        apiParam: 'status',
        comparators: ['in'],
        component: StatusFilter as TBaseFilterComponent,
        componentArgs: {
          hideCount: true,
          possibleValues: isSup
            ? translateFilterOptions(VisibleStatusOptions, this.labelService).filter((o) =>
                SupplierPortalStatuses.includes(o.key)
              )
            : translateFilterOptions(VisibleStatusOptions, this.labelService)
        },
        clearDefaultValue: isSup ? DEFAULT_STATUSES_SUPPLIER : DEFAULT_STATUSES
      },
      {
        queryParam: 'validationStatus',
        apiParam: 'validationStatus',
        comparators: ['in'],
        component: StatusFilter as TBaseFilterComponent,
        componentArgs: {
          hideCount: true,
          possibleValues: POValidationStatusesArray.map((o) => {
            return {
              ...o,
              label: intl.t(sessionUser.user.isSupplier ? o.supplierLabel : o.customerLabel)
            };
          })
        }
      }
    );
    notForSupplier({
      queryParam: 'orderType',
      apiParam: 'orderType',
      comparators: ['in', 'ni'],
      component: TokenFilter as TBaseFilterComponent,
      componentArgs: {
        warehouseIds,
        searchFn: (query: IJsonApiQuery) => {
          return store.customQuery('planningOrders', 'orderTypes', undefined, {
            ...query,
            createdBy
          });
        }
      }
    });

    normalDefs.push(
      {
        queryParam: 'orderNum',
        apiParam: 'orderNum',
        comparators: ['in', 'ni'],
        component: TokenFilter as TBaseFilterComponent,
        componentArgs: {
          warehouseIds,
          searchFn: (query: IJsonApiQuery) => {
            return store.customQuery('planningOrders', 'orderNums', undefined, {
              ...query,
              createdBy
            });
          }
        }
      },
      {
        queryParam: 'externalOrderNum',
        apiParam: 'externalOrderNum',
        comparators: ['in', 'ni'],
        component: TokenFilter as TBaseFilterComponent,
        componentArgs: {
          warehouseIds,
          searchFn: (query: IJsonApiQuery) => {
            return store.customQuery('planningOrders', 'externalOrderNums', undefined, {
              ...query,
              createdBy
            });
          }
        }
      }
    );

    notForSupplier({
      title: this.intl.t('poWorkbench.showOperations'),
      queryParam: 'isOperation',
      apiParam: 'isOperation',
      comparators: ['eq'],
      component: BooleanFilter as TBaseFilterComponent,
      componentArgs: {}
    });

    notForSupplier({
      queryParam: 'partType',
      apiParam: 'partType',
      comparators: ['in', 'ni'],
      component: SkusPartTypeFilter as TBaseFilterComponent,
      componentArgs: {}
    });
    notForSupplier({
      queryParam: 'acqCode',
      apiParam: 'acqCode',
      comparators: ['in'],
      component: SelectFilter as TBaseFilterComponent,
      componentArgs: {
        searchField: 'label',
        possibleValues: translateFilterOptions(AcquisitionCodesOptions, this.labelService)
      }
    });
    normalDefs.push({
      queryParam: 'supplier',
      apiParam: 'supplier',
      comparators: ['in', 'ni'],
      hidden: isSup,
      component: SkusSupplierFilter as TBaseFilterComponent,
      componentArgs: {}
    });
    notForSupplier({
      queryParam: 'workshop',
      apiParam: 'workshop',
      comparators: ['in', 'ni'],
      component: SkusWorkshopFilter as TBaseFilterComponent,
      componentArgs: {
        beforeQuery: beforeQueryWorkshop(
          ['WORKSHOP', 'SUPPLIER', 'WORKSTATION', 'TOOL', 'PERSONNEL'],
          serializeArrayValues(cvFilters['warehouse']?.values ?? [])
        )
      }
    });
    notForSupplier({
      queryParam: 'deliveryWorkshop',
      apiParam: 'deliveryWorkshop',
      comparators: ['in', 'ni'],
      component: SkusWorkshopFilter as TBaseFilterComponent,
      componentArgs: {
        beforeQuery: beforeQueryWorkshop(
          ['DELIVERY'],
          serializeArrayValues(cvFilters['warehouse']?.values ?? [])
        )
      }
    });
    notForSupplier({
      queryParam: 'warehouse',
      apiParam: 'warehouse',
      comparators: ['in', 'ni'],
      component: SkusWarehouseFilter as TBaseFilterComponent,
      componentArgs: {}
    });
    notForSupplier({
      queryParam: 'reorderDate',
      apiParam: 'reorderDate',
      comparators: ['eq', 'lt', 'gt', 'bt'],
      component: DateFilter as TBaseFilterComponent,
      componentArgs: {}
    });
    normalDefs.push({
      queryParam: 'deliveryDate',
      apiParam: 'deliveryDate',
      comparators: ['eq', 'lt', 'gt', 'bt'],
      component: DateFilter as TBaseFilterComponent,
      componentArgs: {}
    });
    normalDefs.push({
      queryParam: 'launchDate',
      apiParam: 'launchDate',
      comparators: ['eq', 'lt', 'gt', 'bt'],
      component: DateFilter as TBaseFilterComponent,
      componentArgs: {}
    });
    notForSupplier({
      queryParam: 'dueDate',
      apiParam: 'dueDate',
      comparators: ['eq', 'lt', 'gt', 'bt'],
      component: DateFilter as TBaseFilterComponent,
      componentArgs: {}
    });
    normalDefs.push({
      queryParam: 'q',
      apiParam: 'q',
      comparators: ['lk'],
      component: InputFilter as TBaseFilterComponent,
      hidden: true,
      componentArgs: { inputType: 'text' }
    });
    notForSupplier({
      queryParam: 'methodology',
      apiParam: 'methodology',
      comparators: ['in'],
      component: SelectFilter as TBaseFilterComponent,
      componentArgs: {
        possibleValues: translateFilterOptions(PlanningMethodologiesOptions, this.labelService),
        searchField: 'label'
      }
    });
    notForSupplier({
      queryParam: 'reorderStrategy',
      apiParam: 'reorderStrategy',
      comparators: ['in'],
      component: SelectFilter as TBaseFilterComponent,
      componentArgs: {
        possibleValues: translateFilterOptions(ReorderStrategiesOptions, this.labelService),
        searchField: 'label'
      }
    });
    notForSupplier({
      queryParam: 'leadTimeCategory',
      apiParam: 'leadTimeCategory',
      comparators: ['in'],
      component: SelectFilter as TBaseFilterComponent,
      componentArgs: {
        possibleValues: translateFilterOptions(LeadTimeCategoriesOptions, this.labelService),
        itemComponent: BufferZoneOptionComponent,
        searchField: 'label'
      }
    });
    notForSupplier({
      queryParam: 'varCategory',
      apiParam: 'varCategory',
      comparators: ['in'],
      component: SelectFilter as TBaseFilterComponent,
      componentArgs: {
        possibleValues: translateFilterOptions(VarCategoriesOptions, this.labelService),
        itemComponent: BufferZoneOptionComponent,
        searchField: 'label'
      }
    });
    notForSupplier({
      queryParam: 'abcClass',
      apiParam: 'abcClass',
      comparators: ['in'],
      component: SelectFilter as TBaseFilterComponent,
      componentArgs: {
        possibleValues: ['A', 'B', 'C', 'D', 'E'].map((v) => ({ key: v }))
      }
    });
    notForSupplier({
      queryParam: 'xyzClass',
      apiParam: 'xyzClass',
      comparators: ['in'],
      component: SelectFilter as TBaseFilterComponent,
      componentArgs: {
        possibleValues: ['X', 'Y', 'Z'].map((v) => ({ key: v }))
      }
    });
    notForSupplier({
      queryParam: 'createdBy',
      apiParam: 'createdBy',
      comparators: ['in', 'ni'],
      component: POUserFilter as TBaseFilterComponent,
      componentArgs: { multiple: true }
    });
    notForSupplier({
      queryParam: 'planningBuffer',
      apiParam: 'planningBuffer',
      comparators: ['in'],
      component: SelectFilter as TBaseFilterComponent,
      componentArgs: {
        possibleValues: translateFilterOptions(BufferZonesOptions, this.labelService),
        itemComponent: BufferZoneOptionComponent,
        searchField: 'label'
      }
    });
    notForSupplier({
      queryParam: 'executionBuffer',
      apiParam: 'executionBuffer',
      comparators: ['in'],
      component: SelectFilter as TBaseFilterComponent,
      componentArgs: {
        possibleValues: translateFilterOptions(BufferZonesOptions, this.labelService),
        itemComponent: BufferZoneOptionComponent,
        searchField: 'label'
      }
    });
    normalDefs.push({
      queryParam: 'priority',
      apiParam: 'priority',
      comparators: ['in'],
      component: PriorityFilter as TBaseFilterComponent,
      componentArgs: {}
    });
    notForSupplier({
      title: intl.t('orderSyncAlert'),
      queryParam: 'syncAlert',
      apiParam: 'syncAlert',
      comparators: ['in'],
      component: SelectFilter as TBaseFilterComponent,
      componentArgs: {
        possibleValues: translateFilterOptions(SyncAlertOptions, this.labelService),
        searchField: 'label',
        itemComponent: AlertOptionComponent,
        valueProp: 'value'
      }
    });
    notForSupplier({
      queryParam: 'schedulingAlert',
      apiParam: 'schedulingAlert',
      comparators: ['in'],
      component: SelectFilter as TBaseFilterComponent,
      componentArgs: {
        possibleValues: translateFilterOptions(SchedulingAlertOptions, this.labelService),
        searchField: 'label',
        itemComponent: AlertOptionComponent,
        valueProp: 'value'
      }
    });
    notForSupplier({
      queryParam: 'timeBufferAlert',
      apiParam: 'timeBufferAlert',
      comparators: ['in'],
      component: SelectFilter as TBaseFilterComponent,
      componentArgs: {
        possibleValues: translateFilterOptions(TimeBufferAlertOptions, this.labelService),
        searchField: 'label',
        itemComponent: AlertOptionComponent,
        valueProp: 'value'
      }
    });
    notForSupplier({
      queryParam: 'syncStatus',
      apiParam: 'syncStatus',
      comparators: ['in'],
      component: SelectFilter as TBaseFilterComponent,
      componentArgs: {
        searchField: 'label',
        itemComponent: AlertOptionComponent,
        valueProp: 'value',
        possibleValues: translateFilterOptions(SyncStatusOptions, this.labelService)
      }
    });
    notForSupplier({
      queryParam: 'supplyAtDate',
      apiParam: 'supplyAtDate',
      comparators: ['eq'],
      component: BooleanFilter as TBaseFilterComponent,
      componentArgs: {}
    });
    notForSupplier({
      queryParam: 'operationReschedulingLocked',
      title: intl.t('operationReschedulingLocked'),
      apiParam: 'reschedulingDisabled',
      comparators: ['eq'],
      component: BooleanFilter as TBaseFilterComponent,
      componentArgs: {}
    });
    notForSupplier({
      queryParam: 'operationStarted',
      title: intl.t('operationStarted'),
      apiParam: 'operationStarted',
      comparators: ['eq'],
      component: BooleanFilter as TBaseFilterComponent,
      componentArgs: {}
    });

    normalDefs.push({
      queryParam: 'reorderQty',
      apiParam: 'reorderQty',
      comparators: ['gt', 'bt', 'lt'],
      component: InputFilter as TBaseFilterComponent,
      componentArgs: {
        inputType: 'number'
      }
    });

    return [
      ...normalDefs,
      ...customFieldDefinitionsService.poCustomFieldsFilters(),
      ...(isSup ? [] : customFieldDefinitionsService.skusCustomFieldsFilters('sku'))
    ];
  }

  buildDefaultView(columns: IBvTableColumn[]): ICreateCollectionViewCmd {
    const { intl, isSup, sessionUser } = this;

    const COLS_TO_KEEP = [
      'acqCode',
      'createdBy',
      'deliveryDate',
      'dueDate',
      'externalOrderNum',
      'orderNum',
      'priority',
      'reorderDate',
      'reorderQty',
      'schedulingAlert',
      'sku',
      'status',
      'syncAlert',
      'timeBufferAlert'
    ];
    const properties = columnsToProps(columns.filter((c) => COLS_TO_KEEP.includes(c.valuePath!)));

    return {
      name: intl.t('collectionViewsPage.default.name', {
        resourceNamePlural: intl.t(POGroupResourceName.plural)
      }),
      description: intl.t('collectionViewsPage.default.description', {
        resourceNamePlural: intl.t(POGroupResourceName.plural)
      }),
      collectionType: 'LIST',
      entityType: POGroupResourceName.plural,
      properties,
      createdBy: sessionUser.userId,
      sortBy: [
        { property: isSup ? 'deliveryDate' : 'dueDate', asc: true },
        { property: 'priority', asc: false },
        { property: 'executionBuffer', asc: true },
        { property: 'sku', asc: true }
      ],
      pageSize: 100,
      groupBy: isSup ? ['orderNum'] : ['orderNum', 'warehouse', 'acqCode', 'supplier', 'workshop'],
      filters: [
        isSup
          ? { property: 'supplier', comparator: 'in', values: sessionUser.user.supplierIds }
          : { property: 'createdBy', comparator: 'in', values: [sessionUser.userId] },
        {
          property: 'status',
          comparator: 'in',
          values: isSup ? DEFAULT_STATUSES_SUPPLIER : DEFAULT_STATUSES
        }
      ].filter(Boolean) as ICollectionFilter[],
      args: isSup ? { ...DefaultSettings, dateField: 'deliveryDate' } : DefaultSettings
    };
  }

  build(queryParams: TQueryParams, sorts: ICollectionSortOption[] = []) {
    const filterDefinitions = this.buildFilters(queryParams);
    const columns = this.buildColumns();
    const defaultView = this.buildDefaultView(columns);

    const view: ICollectionViewDefinition = {
      resourceName: POGroupResourceName,
      sortLimit: 0,
      groupMin: 1,
      sorts: [...columnsToSortOptions(columns), ...sorts] as ICollectionSortOption[],
      properties: columnsToProps(columns),
      filters: filterDefinitions,
      definitions: columns,
      types: this.appSettings.features?.experimental.powGanttView ? ['LIST', 'TIMELINE'] : ['LIST'],
      defaultView,
      argsComponent: POWorkbenchCvArgsEditor
    };

    return view;
  }

  fetchPoViews = restartableTask(async () => {
    if (!this.myCollectionViews) {
      const data = await this.store.rawQuery('collection-view', ['mine', 'poGroups']);
      this.myCollectionViews = normalize('collection-view', data) as CollectionView[];
    }
  });

  private _handleCollectionViewCreated(payload: SingleResourceDoc) {
    if (
      this.myCollectionViews &&
      payload.data.attributes?.['entityType'] === POGroupResourceName.plural
    ) {
      const collectionView = normalize('collection-view', payload) as CollectionView;

      this.myCollectionViews = [...new Set([...(this.myCollectionViews ?? []), collectionView])];
    }
  }

  private _handleCollectionViewUpdated(payload: SingleResourceDoc) {
    if (
      this.myCollectionViews &&
      payload.data.attributes?.['entityType'] === POGroupResourceName.plural
    ) {
      const collectionView = normalize('collection-view', payload) as CollectionView;

      this.myCollectionViews = (this.myCollectionViews ?? []).map((view) => {
        if (collectionView.id === view.id) {
          return collectionView;
        }
        return view;
      });
    }
  }

  private _handleCollectionViewDeleted(payload: SingleResourceDoc) {
    if (
      this.myCollectionViews &&
      payload.data.attributes?.['entityType'] === POGroupResourceName.plural
    ) {
      const collectionView = normalize('collection-view', payload) as CollectionView;

      this.myCollectionViews = (this.myCollectionViews ?? []).filter(
        (view) => collectionView.id !== view.id
      );
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    'collection-builder-po-groups': POGroupsCollectionDefinitionBuilder;
  }
}
